import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiZap } from 'react-icons/fi';
import { MdOutlineCalculate } from 'react-icons/md';
import { apiService } from '../../../components/Config/api.service';
import AmountInput from '../../../components/Utils/AmountInput';
import Modal from '../../../components/Utils/ModalShow';
import { formatMoney } from '../../../components/Utils/FormatUtils';
import {
    GENERIC_FIELDS,
    SIMULATION_MODES,
    buildResultRows,
    buildSimulationPayload,
    isSimulationReady,
    readSimulationResponse,
    sumPayrollVariables,
    withContractElements,
} from './contractSimulation';

const EMPTY_ELEMENTS = { selectable: [], automatic: [], ignored: [] };

const INPUT_CLASS = 'input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20';

const money = (value) => formatMoney(value || 0);

// Lignes qui diminuent le net : affichées en négatif dans la liste.
const DEDUCTION_FIELDS = ['autre_deduction', 'ipm_contribution'];

const sumAmounts = (elements) => elements.reduce((total, element) => total + (Number(element.amount) || 0), 0);

// Bloc repliable : garde l'écran léger, le détail reste à un clic.
const Disclosure = ({ summary, children, defaultOpen = false }) => (
    <details className="group" open={defaultOpen}>
        <summary className="cursor-pointer select-none text-sm text-[--color-primary] hover:underline list-none flex items-center gap-1">
            <span className="transition-transform group-open:rotate-90">›</span>
            {summary}
        </summary>
        <div className="mt-2">{children}</div>
    </details>
);

const ElementRow = ({ name, amount, note }) => (
    <li className="flex items-baseline justify-between gap-3 text-sm text-gray-600">
        <span>
            {name}
            {note && <span className="block text-xs text-amber-700">{note}</span>}
        </span>
        <span className="tabular-nums whitespace-nowrap">{amount}</span>
    </li>
);

const ContractSimulationPanel = ({ contract, employee, onApply, onClose }) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState(SIMULATION_MODES.FROM_NET);
    const [netAmount, setNetAmount] = useState('');
    const [sursalaire, setSursalaire] = useState(contract.sursalaire || 0);
    const [elements, setElements] = useState(EMPTY_ELEMENTS);
    const [elementsError, setElementsError] = useState(false);
    const [excludedKeys, setExcludedKeys] = useState(() => new Set());
    const [manual, setManual] = useState({});
    const [result, setResult] = useState(null);
    const [resultMode, setResultMode] = useState(null);
    const [stale, setStale] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const requestRef = useRef(0);

    const employeeId = employee?.id;
    useEffect(() => {
        if (!employeeId) return;
        let cancelled = false;
        apiService.getElement(`/employee/${employeeId}/simulation_elements`)
            .then((response) => {
                if (!cancelled) setElements({ ...EMPTY_ELEMENTS, ...response.data });
            })
            .catch((fetchError) => {
                console.error('Error fetching simulation elements:', fetchError);
                if (!cancelled) setElementsError(true);
            });
        return () => { cancelled = true; };
    }, [employeeId]);

    const transportLabel = t('contractSimulation.transportPremium');
    const ipmLabel = t('contractSimulation.ipmContribution');
    const { transport_premium: transportPremium, ipm_contribution: ipmContribution } = contract;
    const selectable = useMemo(
        () => withContractElements(
            elements.selectable,
            { transport_premium: transportPremium, ipm_contribution: ipmContribution },
            { transport: transportLabel, ipm: ipmLabel },
        ),
        [elements.selectable, transportPremium, ipmContribution, transportLabel, ipmLabel],
    );

    const fromNet = mode === SIMULATION_MODES.FROM_NET;
    const hasContractFields = contract.convention && contract.category && contract.baseSalary;
    const ready = isSimulationReady({ contract, mode, netAmount });

    // Un résultat affiché ne correspond plus aux paramètres dès qu'on en
    // change un : il reste visible mais signalé, jusqu'au prochain calcul.
    const change = (setter) => (value) => {
        setter(value);
        setStale(true);
    };

    const toggleElement = (key) => {
        setExcludedKeys((previous) => {
            const next = new Set(previous);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
        setStale(true);
    };

    const calculate = async () => {
        const requestId = requestRef.current + 1;
        requestRef.current = requestId;
        setLoading(true);
        setError('');
        const payload = buildSimulationPayload({
            contract, employee, mode, netAmount, sursalaire, selectable, excludedKeys, manual,
        });
        let outcome;
        try {
            const response = await apiService.saveElement('/payor/submit', payload);
            outcome = readSimulationResponse(response.data);
        } catch (requestError) {
            console.error('Error in salary simulation:', requestError);
            outcome = { error: requestError.userMessage || null };
        }
        if (requestRef.current !== requestId) return;
        setResult(outcome.result || null);
        setResultMode(mode);
        setStale(false);
        setError(outcome.result ? '' : (outcome.error || t('contractSimulation.error')));
        setLoading(false);
    };

    const included = selectable.filter((element) => !excludedKeys.has(element.key));
    const totals = sumPayrollVariables(selectable, excludedKeys);
    const includedTotal = totals.transport_premium + totals.prime_imposable
        + totals.prime_non_imposable - totals.autre_deduction - totals.ipm_contribution;
    const hasManual = GENERIC_FIELDS.some((field) => Number(manual[field]) > 0);
    const resultFromNet = resultMode === SIMULATION_MODES.FROM_NET;

    // Le formulaire de contrat vit dans un volet latéral étroit : la fenêtre est
    // rattachée à la page pour s'ouvrir en pleine largeur, et marquée pour que
    // le volet ne la prenne pas pour un clic extérieur.
    return createPortal(
        <div data-keep-drawer-open>
            <Modal
                isOpen
                onClose={onClose}
                title={t('contractSimulation.title')}
                subtitle={t('contractSimulation.subtitle')}
                headerIcon={<MdOutlineCalculate className="text-[--color-primary]" size={22} />}
                size={980}
            >
                <div
                    data-testid="contract-simulation-panel"
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 max-h-[72vh] overflow-y-auto pr-1"
                >
                    {/* Paramètres */}
                    <div className="space-y-5">
                        <div>
                            <div className="join w-full mb-3">
                                {[SIMULATION_MODES.FROM_NET, SIMULATION_MODES.FROM_SURSALAIRE].map((value) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => change(setMode)(value)}
                                        aria-pressed={mode === value}
                                        className={`btn btn-sm join-item flex-1 ${mode === value ? 'bg-[--color-primary] text-white hover:bg-[--bg-hover]' : ''}`}
                                    >
                                        {t(`contractSimulation.mode.${value}`)}
                                    </button>
                                ))}
                            </div>
                            <label className="block">
                                <span className="text-sm font-medium text-gray-700">
                                    {t(fromNet ? 'contractSimulation.desiredNet' : 'contractSimulation.sursalaire')}
                                </span>
                                {fromNet ? (
                                    <AmountInput value={netAmount} onChange={change(setNetAmount)} className={INPUT_CLASS} />
                                ) : (
                                    <AmountInput value={sursalaire} onChange={change(setSursalaire)} className={INPUT_CLASS} />
                                )}
                            </label>
                        </div>

                        <div className="rounded-lg border border-gray-200 p-4">
                            <div className="flex items-baseline justify-between mb-2">
                                <p className="text-sm font-medium text-gray-700">{t('contractSimulation.elementsTitle')}</p>
                                {selectable.length > 0 && (
                                    <p className="text-xs text-gray-500">
                                        {t('contractSimulation.includedSummary', {
                                            count: included.length, total: selectable.length, amount: money(includedTotal),
                                        })}
                                    </p>
                                )}
                            </div>
                            {elementsError && (
                                <p role="alert" className="text-sm text-red-600 mb-2">{t('contractSimulation.elementsError')}</p>
                            )}
                            {!selectable.length && !elementsError && (
                                <p className="text-sm text-gray-500">{t('contractSimulation.noElements')}</p>
                            )}
                            <ul className="space-y-1.5">
                                {selectable.map((element) => (
                                    <li key={element.key}>
                                        <label className="flex items-center justify-between gap-3 text-sm cursor-pointer">
                                            <span className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm mr-2"
                                                    checked={!excludedKeys.has(element.key)}
                                                    onChange={() => toggleElement(element.key)}
                                                />
                                                {element.name}
                                                {element.estimation && (
                                                    <span className="ml-2 text-xs text-amber-700">
                                                        {t('contractSimulation.estimation')}
                                                    </span>
                                                )}
                                            </span>
                                            <span className="tabular-nums whitespace-nowrap">
                                                {money(DEDUCTION_FIELDS.includes(element.field) ? -element.amount : element.amount)}
                                            </span>
                                        </label>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-3 space-y-2">
                                {elements.automatic.length > 0 && (
                                    <Disclosure
                                        summary={t('contractSimulation.automaticSummary', {
                                            count: elements.automatic.length, amount: money(sumAmounts(elements.automatic)),
                                        })}
                                    >
                                        <p className="text-xs text-gray-500 mb-1">{t('contractSimulation.automaticHint')}</p>
                                        <ul className="space-y-1">
                                            {elements.automatic.map((element) => (
                                                <ElementRow
                                                    key={element.key}
                                                    name={element.name}
                                                    amount={element.computed ? t('contractSimulation.computed') : money(element.amount)}
                                                    note={element.estimation ? t('contractSimulation.individualAmount', {
                                                        amount: money(element.individualAmount),
                                                    }) : null}
                                                />
                                            ))}
                                        </ul>
                                    </Disclosure>
                                )}
                                {elements.ignored.length > 0 && (
                                    <Disclosure
                                        summary={t('contractSimulation.ignoredSummary', { count: elements.ignored.length })}
                                    >
                                        <ul className="space-y-1">
                                            {elements.ignored.map((element) => (
                                                <ElementRow
                                                    key={element.key}
                                                    name={element.name}
                                                    amount={element.computed ? t('contractSimulation.computed') : money(element.amount)}
                                                />
                                            ))}
                                        </ul>
                                    </Disclosure>
                                )}
                                <Disclosure summary={t('contractSimulation.manualTitle')} defaultOpen={hasManual}>
                                    <p className="text-xs text-gray-500 mb-2">{t('contractSimulation.manualHint')}</p>
                                    <div className="grid grid-cols-3 gap-2">
                                        {GENERIC_FIELDS.map((field) => (
                                            <label key={field} className="block">
                                                <span className="text-xs text-gray-600">{t(`contractSimulation.manual.${field}`)}</span>
                                                <AmountInput
                                                    value={manual[field] ?? ''}
                                                    onChange={(value) => change(setManual)({ ...manual, [field]: value })}
                                                    className="input input-bordered input-sm w-full"
                                                    placeholder="0"
                                                />
                                            </label>
                                        ))}
                                    </div>
                                </Disclosure>
                            </div>
                        </div>

                        {!hasContractFields && (
                            <p className="text-sm text-gray-500">{t('contractSimulation.missingContractFields')}</p>
                        )}
                        <button
                            type="button"
                            onClick={calculate}
                            disabled={!ready || loading}
                            className="btn w-full text-white bg-[--color-primary] hover:bg-[--bg-hover] disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                    {t('contractSimulation.calculating')}
                                </>
                            ) : (
                                <>
                                    <MdOutlineCalculate className="mr-2" size={18} />
                                    {t('contractSimulation.calculate')}
                                </>
                            )}
                        </button>
                    </div>

                    {/* Résultat */}
                    <div aria-live="polite" className="rounded-lg bg-gray-50 border border-gray-200 p-5 flex flex-col">
                        {error && !loading && (
                            <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3 mb-3">
                                {error}
                            </p>
                        )}
                        {!result && !error && (
                            <p className="text-sm text-gray-500 m-auto text-center max-w-[260px]">
                                {t('contractSimulation.emptyResult')}
                            </p>
                        )}
                        {result && (
                            <div className={stale || loading ? 'opacity-50' : ''}>
                                <p className="text-sm text-gray-600">
                                    {t(resultFromNet ? 'contractSimulation.headline.sursalaire' : 'contractSimulation.headline.net')}
                                </p>
                                <p className="text-3xl font-semibold text-[--color-primary] tabular-nums">
                                    {money(resultFromNet ? result.additional_salary : result.net_salary)}
                                </p>
                                <p className="text-sm text-gray-600 mt-1">
                                    {t('contractSimulation.headline.secondary', {
                                        gross: money(result.gross_salary), net: money(result.net_salary),
                                    })}
                                </p>

                                <table className="w-full text-sm mt-4">
                                    <tbody>
                                        {buildResultRows(result).map(({ key, value, strong, detail }) => (
                                            <tr
                                                key={key}
                                                className={`${strong ? 'font-semibold border-t border-gray-300' : ''} ${detail ? 'text-xs text-gray-500' : ''}`}
                                            >
                                                <td className={`py-1 ${detail ? 'pl-4' : 'text-gray-700'}`}>
                                                    {t(`contractSimulation.result.${key}`)}
                                                </td>
                                                <td className="py-1 text-right tabular-nums">{money(value)}</td>
                                            </tr>
                                        ))}
                                        <tr className="font-semibold border-t border-gray-300">
                                            <td className="py-1">{t('contractSimulation.result.net')}</td>
                                            <td className="py-1 text-right tabular-nums">{money(result.net_salary)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {result && stale && !loading && (
                            <p className="text-sm text-amber-700 mt-3">{t('contractSimulation.stale')}</p>
                        )}
                        {result && (
                            <div className="mt-auto pt-4">
                                <button
                                    type="button"
                                    disabled={loading || stale}
                                    onClick={() => onApply(result.additional_salary)}
                                    className="btn h-auto min-h-[3rem] py-2 w-full bg-[--color-success] hover:bg-green-600 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <FiZap className="mr-2" size={16} />
                                    {t('contractSimulation.apply', { amount: money(result.additional_salary) })}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </Modal>
        </div>,
        document.body,
    );
};

export default ContractSimulationPanel;
